import React, { useState, useEffect } from 'react';
import {
  getFinancialBid,
  updateFinancialBid,
  uploadFinancialBidPdf,
  uploadFinancialBidExcel,
  approveFinancialBid,
  rejectFinancialBid,
  getFinancialBidAudit,
  getFinancialBidDocumentSignedUrl,
} from '../services/api';
import { formatDate } from '../utils/formatDate';
import './TechnicalBidDetail.css';
import './TenderList.css';

// eslint-disable-next-line no-unused-vars
const STATUS_CLASS = {
  assigned: 'status-participating',
  in_progress: 'status-participating',
  submitted: 'status-submitted',
  approved: 'status-won',
  rejected: 'status-lost',
};

function FinancialBidDetail({ financialBidId, user, canManage, onClose }) {
  const [fb, setFb] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [pdfFile, setPdfFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [statusDropdownValue, setStatusDropdownValue] = useState('');
  const [documentLoading, setDocumentLoading] = useState(null);

  const isAssignedToMe = user?.id && fb?.assigned_to_id === user.id;
  const canFinanceUserAct = isAssignedToMe && ['assigned', 'in_progress'].includes(fb?.status);
  // eslint-disable-next-line no-unused-vars
  const canTenderManagerAct = canManage && fb?.status === 'submitted';

  useEffect(() => {
    if (!financialBidId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getFinancialBid(financialBidId),
      getFinancialBidAudit(financialBidId),
    ])
      .then(([res1, res2]) => {
        if (cancelled) return;
        if (res1.success) setFb(res1.financial_bid);
        if (res2.success) setAudit(res2.audits || []);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [financialBidId]);

  const refresh = () => {
    getFinancialBid(financialBidId).then((res) => {
      if (res.success) setFb(res.financial_bid);
    });
    getFinancialBidAudit(financialBidId).then((res) => {
      if (res.success) setAudit(res.audits || []);
    });
  };

  const handleStart = async () => {
    if (!fb || fb.status !== 'assigned') return;
    try {
      setActionLoading(true);
      const res = await updateFinancialBid(fb.id, { status: 'in_progress' });
      if (res.success) setFb(res.financial_bid);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!fb) return;
    if (!fb.has_pdf) {
      setError('PDF must be uploaded before submitting');
      return;
    }
    try {
      setActionLoading(true);
      const res = await updateFinancialBid(fb.id, { status: 'submitted' });
      if (res.success) setFb(res.financial_bid);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!fb) return;
    try {
      setActionLoading(true);
      setStatusDropdownValue('');
      const res = await approveFinancialBid(fb.id);
      if (res.success) setFb(res.financial_bid);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusDropdownChange = async (e) => {
    const value = e.target.value;
    if (!value || value === fb?.status) return;
    setStatusDropdownValue('');
    if (value === 'approved') {
      handleApprove();
      return;
    }
    if (value === 'rejected') {
      setShowRejectModal(true);
      return;
    }
    // assigned | in_progress | submitted – update via API (allows reopen)
    try {
      setActionLoading(true);
      const res = await updateFinancialBid(fb.id, { status: value });
      if (res.success) setFb(res.financial_bid);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!fb) return;
    try {
      setActionLoading(true);
      const res = await rejectFinancialBid(fb.id, { feedback: rejectFeedback || undefined });
      if (res.success) {
        setFb(res.financial_bid);
        setShowRejectModal(false);
        setRejectFeedback('');
      }
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUploadPdf = async () => {
    if (!pdfFile || !fb) return;
    setUploadError(null);
    try {
      setActionLoading(true);
      const res = await uploadFinancialBidPdf(fb.id, pdfFile);
      if (res.success) {
        setFb(res.financial_bid);
        setPdfFile(null);
      }
      refresh();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUploadExcel = async () => {
    if (!excelFile || !fb) return;
    setUploadError(null);
    try {
      setActionLoading(true);
      const res = await uploadFinancialBidExcel(fb.id, excelFile);
      if (res.success) {
        setFb(res.financial_bid);
        setExcelFile(null);
      }
      refresh();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="tb-detail-overlay" onClick={onClose}>
        <div className="tb-detail-modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="tb-detail-close" onClick={onClose}>×</button>
          <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
        </div>
      </div>
    );
  }

  if (error && !fb) {
    return (
      <div className="tb-detail-overlay" onClick={onClose}>
        <div className="tb-detail-modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="tb-detail-close" onClick={onClose}>×</button>
          <div className="error-message">{error}</div>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  if (!fb) return null;

  return (
    <div className="tb-detail-overlay" onClick={onClose}>
      <div className="tb-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tb-detail-header">
          <div className="tb-detail-tabs">
            <span className={`tb-detail-tab active`}>{fb.status_display || fb.status}</span>
            <span className="tb-detail-tab">Price Bid</span>
          </div>
          <button type="button" className="tb-detail-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="tb-detail-body">
          {error && (
            <div className="error-message">
              {error}
              <button type="button" onClick={() => setError(null)}>×</button>
            </div>
          )}

          <h2 className="tb-detail-id">{fb.tender_ref || fb.tender_id}</h2>
          {fb.tender_title && <p className="tb-detail-title">{fb.tender_title}</p>}

          <div className="tb-detail-grid">
            <div className="tb-detail-item">
              <label>Buyer</label>
              <span>{fb.buyer_name || '-'}</span>
            </div>
            <div className="tb-detail-item">
              <label>Assigned To</label>
              <span>{fb.assigned_to_name || '-'}</span>
            </div>
            <div className="tb-detail-item">
              <label>Due Date</label>
              <span>{formatDate(fb.due_date)}</span>
            </div>
            <div className="tb-detail-item">
              <label>Submission Deadline</label>
              <span>{formatDate(fb.submission_deadline)}</span>
            </div>
            <div className="tb-detail-item">
              <label>Assigned By</label>
              <span>{fb.assigned_by_name || '-'}</span>
            </div>
          </div>
        </div>

        {/* Status Actions */}
        <div className="tb-detail-actions">
          {canFinanceUserAct && fb.status === 'assigned' && (
            <button className="confirm-go-ahead-btn" onClick={handleStart} disabled={actionLoading}>
              {actionLoading ? '...' : 'Start'}
            </button>
          )}
          {canFinanceUserAct && fb.status === 'in_progress' && (
            <button
              className="confirm-go-ahead-btn"
              onClick={handleSubmit}
              disabled={actionLoading || !fb.has_pdf}
              title={!fb.has_pdf ? 'Upload PDF first' : ''}
            >
              {actionLoading ? '...' : 'Submit'}
            </button>
          )}
          {canManage && (
            <div className="tb-detail-status-dropdown-wrap">
              <label htmlFor="fb-status-dropdown" className="tb-detail-status-dropdown-label">Status</label>
              <select
                id="fb-status-dropdown"
                className="tb-detail-status-dropdown"
                value={fb?.status || ''}
                onChange={handleStatusDropdownChange}
                disabled={actionLoading}
                aria-label="Financial bid status"
              >
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="tb-detail-section">
          <div className="tb-detail-nav">
            {['info', 'files', 'audit'].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`tb-detail-nav-btn ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'info' ? 'Task Info' : tab === 'files' ? 'Files' : 'Audit Trail'}
              </button>
            ))}
          </div>

          {activeTab === 'info' && (
            <div>
              <label className="tb-detail-instructions-label">Instructions</label>
              <p className="tb-detail-instructions-content">{fb.instructions || '–'}</p>
              {fb.rejection_feedback && (
                <div className="tb-detail-item" style={{ marginTop: '16px' }}>
                  <label>Rejection Feedback</label>
                  <p style={{ margin: '4px 0 0 0', color: '#b91c1c', whiteSpace: 'pre-wrap', fontWeight: 500 }}>{fb.rejection_feedback}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div>
              {uploadError && (
                <div className="error-message" style={{ marginBottom: '12px' }}>
                  {uploadError}
                  <button type="button" onClick={() => setUploadError(null)}>×</button>
                </div>
              )}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>PDF (required)</label>
                {fb.has_pdf ? (
                  <p>
                    <button
                      type="button"
                      className="link-btn"
                      disabled={documentLoading === 'pdf'}
                      onClick={async () => {
                        setDocumentLoading('pdf');
                        try {
                          const url = await getFinancialBidDocumentSignedUrl(fb.id, 'pdf');
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch (e) {
                          setError(e.message || 'Failed to open PDF');
                        } finally {
                          setDocumentLoading(null);
                        }
                      }}
                    >
                      {documentLoading === 'pdf' ? 'Opening...' : 'Download PDF'}
                    </button>
                    {canFinanceUserAct && (
                      <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>or replace:</span>
                    )}
                  </p>
                ) : null}
                {canFinanceUserAct && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    />
                    <button
                      className="edit-btn"
                      onClick={handleUploadPdf}
                      disabled={!pdfFile || actionLoading}
                    >
                      {actionLoading ? 'Uploading...' : 'Upload PDF'}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Excel (optional)</label>
                {fb.has_excel ? (
                  <p>
                    <button
                      type="button"
                      className="link-btn"
                      disabled={documentLoading === 'excel'}
                      onClick={async () => {
                        setDocumentLoading('excel');
                        try {
                          const url = await getFinancialBidDocumentSignedUrl(fb.id, 'excel');
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch (e) {
                          setError(e.message || 'Failed to open Excel');
                        } finally {
                          setDocumentLoading(null);
                        }
                      }}
                    >
                      {documentLoading === 'excel' ? 'Opening...' : 'Download Excel'}
                    </button>
                    {canFinanceUserAct && (
                      <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>or replace:</span>
                    )}
                  </p>
                ) : null}
                {canFinanceUserAct && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                    <input
                      type="file"
                      accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
                    />
                    <button
                      className="edit-btn"
                      onClick={handleUploadExcel}
                      disabled={!excelFile || actionLoading}
                    >
                      {actionLoading ? 'Uploading...' : 'Upload Excel'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div>
              {audit.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No audit entries yet.</p>
              ) : (
                <ul className="tb-detail-audit-list">
                  {audit.map((a, i) => (
                    <li key={a.id || i} className="tb-detail-audit-item">
                      <strong>{a.action}</strong>
                      {a.performed_by_name && <span> by {a.performed_by_name}</span>}
                      <span className="tb-detail-audit-meta">{formatDate(a.created_at)}</span>
                      {(a.previous_value || a.new_value || a.details) && (
                        <div className="tb-detail-audit-details">
                          {a.previous_value && a.new_value && (
                            <span>{a.previous_value} → {a.new_value}</span>
                          )}
                          {a.details && <span>{a.details}</span>}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => !actionLoading && setShowRejectModal(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reject Price Bid</h3>
            <p>Provide optional feedback for the finance user:</p>
            <textarea
              value={rejectFeedback}
              onChange={(e) => setRejectFeedback(e.target.value)}
              placeholder="Feedback (optional)"
              rows={3}
              style={{ width: '100%', margin: '12px 0', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowRejectModal(false)} disabled={actionLoading}>Cancel</button>
              <button className="cancel-btn" style={{ background: '#b91c1c', color: 'white' }} onClick={handleReject} disabled={actionLoading}>
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FinancialBidDetail;
